import { useCallback, useEffect, useRef, useState } from 'react';
import { useDebouncedCallback } from '@/hooks/useDebouncedCallback';

/**
 * スクロール位置を表す型
 */
type ScrollPosition = 'left' | 'middle' | 'right';

/**
 * スクロール位置の計算関数（左右端判定は epsilon ≤1）
 */
export function calculatePosition(props: {
  scrollWidth: number;
  clientWidth: number;
  scrollLeft: number;
}): ScrollPosition | undefined {
  const {
    scrollWidth,
    clientWidth,
    scrollLeft
  } = props;
  if (scrollWidth <= clientWidth) return undefined;
  if (scrollLeft <= 1) return 'left';
  if (scrollWidth - scrollLeft - clientWidth < 1) return 'right';
  return 'middle';
}

/**
 * スクロール可能な要素のナビゲーション機能を提供するカスタムフック
 *
 * - スクロール位置（left/middle/right）の判定
 * - 前/次へのスクロール（表示領域幅ぶん）
 * - ResizeObserver による container/content のサイズ変化追従
 *
 * @returns スクロール状態と制御オブジェクト
 */
export function useScrollableNavigation() {
  // 現在のスクロール位置状態
  const [position, setPosition] = useState<ScrollPosition | undefined>(undefined);

  // コンテナ（スクロールviewport）とコンテンツ、リサイズオブザーバーの参照
  const containerRef = useRef<HTMLDivElement>(null);
  const [contentElement, setContentElement] = useState<HTMLDivElement | null>(null);
  const observerRef = useRef<ResizeObserver | null>(null);

  // アンマウント状態を追跡するrefを用意
  const isMountedRef = useRef(true);

  /**
   * スクロール位置の更新処理
   */
  const updatePosition = useCallback(() => {
    // コンポーネントがアンマウントされている場合は更新しない
    if (!isMountedRef.current || !containerRef.current) return;
    const container = containerRef.current;
    const {
      scrollLeft,
      scrollWidth,
      clientWidth
    } = container;
    const newPosition = calculatePosition({
      scrollWidth,
      clientWidth,
      scrollLeft
    });
    setPosition(newPosition);
  }, []);
  const handleScroll = useDebouncedCallback(updatePosition, [], {
    delay: 50
  });

  /**
   * content要素（幅変化を監視したい要素）の ref コールバック
   *
   * - object ref（RefObject）は型が不変で扱いにくいため、コールバックrefとして公開する
   * - containerとは別要素を監視したい場合（件数表示などの後追い幅変化）に利用する
   */
  const contentRef = useCallback((node: HTMLDivElement | null) => {
    setContentElement(node);
  }, []);

  // サイズ変更の監視設定
  useEffect(() => {
    isMountedRef.current = true;

    // ResizeObserverで container/content のサイズ変更を監視
    observerRef.current = new ResizeObserver(() => {
      if (isMountedRef.current) updatePosition();
    });
    const observer = observerRef.current;
    const container = containerRef.current;
    const content = contentElement;
    if (container) observer.observe(container);
    if (content) observer.observe(content);
    if (container) {
      // 初期表示時にもポジションを更新
      requestAnimationFrame(() => {
        if (isMountedRef.current) updatePosition();
      });
    }
    return () => {
      isMountedRef.current = false;
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    };
  }, [updatePosition, contentElement]);

  /**
   * 前の要素にスクロール
   */
  const scrollToPrevious = useCallback(() => {
    if (!containerRef.current) return;
    const container = containerRef.current;
    container.scrollTo({
      left: container.scrollLeft - container.clientWidth,
      behavior: 'smooth'
    });
  }, []);

  /**
   * 次の要素にスクロール
   */
  const scrollToNext = useCallback(() => {
    if (!containerRef.current) return;
    const container = containerRef.current;
    container.scrollTo({
      left: container.scrollLeft + container.clientWidth,
      behavior: 'smooth'
    });
  }, []);
  return {
    /** スクロール可能なviewport要素のref */
    containerRef,
    /** content要素（幅変化を監視したい要素）のref（コールバックref） */
    contentRef,
    /** 現在のスクロール位置 */
    position,
    /** onScroll に渡すハンドラ（debounced） */
    handleScroll,
    /** 前へ（表示領域幅ぶん） */
    scrollToPrevious,
    /** 次へ（表示領域幅ぶん） */
    scrollToNext,
    /** 現在位置を強制更新（初期化/非同期更新の後に呼べる） */
    refreshPosition: updatePosition,
    /** 左端（またはスクロール不要） */
    isAtStart: position === undefined || position === 'left',
    /** 右端（またはスクロール不要） */
    isAtEnd: position === undefined || position === 'right'
  };
}